<template>
  <div class="oracle-admin-layout">
    <!-- 오라클 스타일 헤더 -->
    <header class="oracle-header">
      <!-- 상단 유틸리티 바 -->
      <div class="header-utility-bar">
        <div class="utility-left">
          <span class="system-status">
            <i class="bi bi-circle-fill status-dot"></i>
            시스템 정상 운영중
          </span>
        </div>
        <div class="utility-right">
          <!-- 🔥 2026-10-21 지금 처리할 일 배지 (1분마다 갱신, 실패하면 숨김) -->
          <div v-if="headerCounts" class="todo-badges">
            <router-link v-for="b in headerBadges" :key="b.key" :to="b.to" class="todo-badge"
                         :class="{ 'todo-badge--hot': b.hot && b.count, 'todo-badge--zero': !b.count }" :title="b.title">
              <span>{{ b.label }}</span><b>{{ b.count }}</b>
            </router-link>
          </div>
          <span class="current-time">
            <i class="bi bi-clock"></i>
            {{ currentTime }}
          </span>
          <span class="admin-name">
            <i class="bi bi-person-circle"></i>
            관리자
          </span>
          <button class="utility-btn" @click="logout">
            <i class="bi bi-box-arrow-right"></i>
            로그아웃
          </button>
        </div>
      </div>

      <!-- 메인 헤더 -->
      <div class="header-main">
        <div class="header-brand">
          <router-link to="/admin" class="brand-link">
            <div class="brand-icon">
              <i class="bi bi-layers-fill"></i>
            </div>
            <div class="brand-text">
              <span class="brand-title">Doctor Market</span>
              <span class="brand-subtitle">Admin Console</span>
            </div>
          </router-link>
        </div>

        <!-- 메인 네비게이션 -->
        <nav class="header-nav">
          <div class="nav-items">
            <a
              v-for="(menu, index) in mainMenus"
              :key="index"
              href="#"
              class="nav-item"
              :class="{ active: activeMenu === menu.id }"
              @click.prevent="setActiveMenu(menu)"
            >
              <i :class="menu.icon"></i>
              <span>{{ menu.name }}</span>
            </a>
          </div>
        </nav>

        <!-- 모바일 메뉴 토글 -->
        <button class="mobile-menu-toggle" @click="toggleMobileMenu">
          <i class="bi bi-list"></i>
        </button>
      </div>

      <!-- 모바일 메뉴 -->
      <div class="mobile-nav" :class="{ 'show': showMobileMenu }">
        <a
          v-for="(menu, index) in mainMenus"
          :key="'mobile-' + index"
          href="#"
          class="mobile-nav-item"
          :class="{ active: activeMenu === menu.id }"
          @click.prevent="setActiveMenu(menu); showMobileMenu = false"
        >
          <i :class="menu.icon"></i>
          <span>{{ menu.name }}</span>
        </a>
      </div>
    </header>

    <!-- 메인 컨테이너 -->
    <div class="oracle-main-container">
      <!-- 사이드바 -->
      <!-- 🔥 2026-10-21 왼쪽 메뉴 접기·펼치기 (PC 전용, 본문 넓게). 상태는 브라우저에 기억 -->
      <button v-if="sidebarCollapsed" type="button" class="sidebar-open-tab" @click="toggleSidebar" title="메뉴 펼치기">
        <i class="bi bi-layout-sidebar"></i><span>메뉴</span>
      </button>
      <aside class="oracle-sidebar" :class="{ 'is-collapsed': sidebarCollapsed }">
        <div class="sidebar-header">
          <i :class="getActiveMenuIcon()"></i>
          <span>{{ getActiveMenuName() }}</span>
          <button type="button" class="sidebar-collapse-btn" @click="toggleSidebar" title="메뉴 접기">
            <i class="bi bi-chevron-double-left"></i>
          </button>
        </div>
        <!-- 🔥 2026-10-21 대시보드: 자주 가는 메뉴를 2열 타일(아이콘 + 작은 제목)로 -->
        <nav v-if="activeMenu === 'home'" class="sidebar-nav">
          <!-- 위 4개만 2열 타일 -->
          <div class="quick-tiles">
            <router-link
              v-for="(subMenu, index) in getSubMenus().slice(0, 4)"
              :key="'tile-' + index"
              :to="subMenu.path"
              class="quick-tile"
              exact-active-class="quick-tile--on"
            >
              <i :class="subMenu.icon.replace(' me-2', '')"></i>
              <span>{{ subMenu.name }}</span>
            </router-link>
          </div>
          <!-- 나머지는 기존 한 줄 목록 그대로 -->
          <router-link
            v-for="(subMenu, index) in getSubMenus().slice(4)"
            :key="'row-' + index"
            :to="subMenu.path"
            class="sidebar-link"
            active-class="active"
          >
            <i :class="subMenu.icon"></i>
            <span>{{ subMenu.name }}</span>
            <i class="bi bi-chevron-right arrow-icon"></i>
          </router-link>
        </nav>
        <nav v-else class="sidebar-nav">
          <router-link
            v-for="(subMenu, index) in getSubMenus()"
            :key="index"
            :to="subMenu.path"
            class="sidebar-link"
            active-class="active"
          >
            <i :class="subMenu.icon"></i>
            <span>{{ subMenu.name }}</span>
            <i class="bi bi-chevron-right arrow-icon"></i>
          </router-link>
        </nav>
        <!-- 🔥 2026-10-21 대시보드 전용: 사이트 바로가기 배너 -->
        <div v-if="activeMenu === 'home'" class="dm-side-banner">
          <div class="dm-side-banner-title"><i class="bi bi-box-arrow-up-right"></i> 바로가기</div>
          <a :href="siteUrl('/')" target="_blank" rel="noopener" class="dm-side-banner-link"><i class="bi bi-shop"></i> 쇼핑몰 열기</a>
          <a :href="siteUrl('/seller')" target="_blank" rel="noopener" class="dm-side-banner-link"><i class="bi bi-building"></i> 입점사 센터</a>
          <a :href="siteUrl('/partner')" target="_blank" rel="noopener" class="dm-side-banner-link"><i class="bi bi-person-badge"></i> 영업사원 포털</a>
        </div>
        <div class="sidebar-footer">
          <div class="quick-stats">
            <div class="stat-item">
              <i class="bi bi-speedometer2"></i>
              <span>빠른 접근</span>
            </div>
          </div>
        </div>
      </aside>

      <!-- 메인 콘텐츠 -->
      <main class="oracle-content">
        <div class="content-wrapper">
          <router-view />
        </div>
      </main>
    </div>

    <!-- 푸터 -->
    <footer class="oracle-footer">
      <div class="footer-content">
        <div class="footer-left">
          <div class="footer-brand">
            <i class="bi bi-layers-fill"></i>
            <div class="footer-brand-text">
              <span class="footer-title">Doctor Market ERP</span>
              <span class="footer-subtitle">Enterprise Resource Planning</span>
            </div>
          </div>
        </div>
        <div class="footer-center">
          <div class="footer-status">
            <div class="status-badge online">
              <i class="bi bi-shield-check"></i>
              <span>보안 연결</span>
            </div>
            <div class="status-badge">
              <i class="bi bi-hdd-stack"></i>
              <span>서버 정상</span>
            </div>
            <div class="status-badge version">
              <span>v2.1.0</span>
            </div>
          </div>
        </div>
        <div class="footer-right">
          <div class="footer-links">
            <a href="#">도움말</a>
            <a href="#">문의하기</a>
            <a href="#">이용약관</a>
          </div>
          <p class="copyright">© 2024 Doctor Market. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'AdminLayout',
  data() {
    return {
      activeMenu: 'config',
      showMobileMenu: false,
      headerCounts: null,   // 🔥 2026-10-21 상단 '처리할 일' 배지 숫자 (null 이면 배지 숨김)
      sidebarCollapsed: (() => { try { return localStorage.getItem('admin.sidebar.collapsed') === '1'; } catch (e) { return false; } })(),
      currentTime: '',
      mainMenus: [
        { id: 'config', name: '환경설정', icon: 'bi bi-gear' },
        { id: 'vendor', name: '거래처관리', icon: 'bi bi-building' },
        { id: 'seller', name: '입점사관리', icon: 'bi bi-shop' },
        { id: 'product', name: '제품관리', icon: 'bi bi-box' },
        { id: 'inventory', name: '재고관리', icon: 'bi bi-stack' },
        { id: 'shoporders', name: '주문관리', icon: 'bi bi-cart' },
        { id: 'promotion', name: '프로모션/쿠폰', icon: 'bi bi-tags' },
        { id: 'design', name: '디자인관리', icon: 'bi bi-palette' },
        { id: 'payment', name: '정산 및 외상결제관리', icon: 'bi bi-credit-card' },
        { id: 'stats', name: '매출/통계', icon: 'bi bi-graph-up' },
        { id: 'support', name: '고객지원', icon: 'bi bi-headset' }
      ],
      subMenus: {
        // 🔥 2026-10-21 대시보드(/admin)에서만 보이는 '자주 가는 메뉴' — 상단 메뉴에는 넣지 않는다(상단 줄 넘침 방지)
        home: [
          { name: '대시보드', path: '/admin', icon: 'bi bi-speedometer2 me-2' },
          { name: '주문 목록', path: '/admin/shoporders', icon: 'bi bi-list-ul me-2' },
          { name: '신규 주문', path: '/admin/shoporders/new', icon: 'bi bi-plus-circle me-2' },
          { name: '배송 준비중', path: '/admin/shoporders/preparing', icon: 'bi bi-box-seam me-2' },
          { name: '취소/반품/교환', path: '/admin/shoporders/cancelled', icon: 'bi bi-x-circle me-2' },
          { name: '거래처 목록', path: '/admin/vendor/list', icon: 'bi bi-building me-2' },
          { name: '제품 목록', path: '/admin/product/list', icon: 'bi bi-grid me-2' },
          { name: '재고 현황', path: '/admin/inventory/status', icon: 'bi bi-clipboard-data me-2' },
          { name: '가나전산 연동', path: '/admin/inventory/gana', icon: 'bi bi-hdd-network me-2' },
          { name: '용마로지스 배송조회', path: '/admin/inventory/yongma', icon: 'bi bi-truck me-2' },
          { name: '원장 미수금 결제', path: '/admin/payment/ledger-payments', icon: 'bi bi-credit-card-2-front me-2' },
          { name: '입점사 판매 정산', path: '/admin/seller/settlement', icon: 'bi bi-cash-stack me-2' },
          { name: '1:1 문의 관리', path: '/admin/support/inquiry', icon: 'bi bi-chat-dots me-2' },
          { name: '매출 통계', path: '/admin/stats/sales', icon: 'bi bi-bar-chart-line me-2' }
        ],
        config: [
          { name: '기본 설정', path: '/admin/config/basic', icon: 'bi bi-sliders me-2' },
          { name: '사용자 관리', path: '/admin/config/users', icon: 'bi bi-people me-2' },
          { name: '권한 설정', path: '/admin/config/permissions', icon: 'bi bi-shield-lock me-2' },
          { name: '시스템 설정', path: '/admin/config/system', icon: 'bi bi-gear-wide-connected me-2' },
          { name: '휴일 관리', path: '/admin/config/holidays', icon: 'bi bi-calendar-event me-2' },
          { name: '알림 관리', path: '/admin/config/notifications', icon: 'bi bi-bell me-2' }
        ],
        vendor: [
          { name: '거래처 목록', path: '/admin/vendor/list', icon: 'bi bi-list-ul me-2' },
          // { name: '거래처 등록', path: '/admin/vendor/register', icon: 'bi bi-plus-circle me-2' },
          { name: '거래처 등급', path: '/admin/vendor/level', icon: 'bi bi-plus-circle me-2' },
          { name: '구매수량 제한', path: '/admin/vendor/purchase-limit', icon: 'bi bi-slash-circle me-2' },
          { name: '초대링크 관리', path: '/admin/invite-tokens', icon: 'bi bi-link-45deg me-2' },
          // 영업사원 파트너 — 2026-08-21 추가
          { name: '영업사원 파트너', path: '/admin/partner/list', icon: 'bi bi-person-badge me-2' },
          // 🔥 2026-09-16 본사가 쓰고 영업사원이 읽는 공지 게시판
          { name: '영업사원 공지', path: '/admin/partner/notices', icon: 'bi bi-megaphone me-2' },
          { name: '예상 정산 내역', path: '/admin/partner/settlements', icon: 'bi bi-calculator me-2' }
        ],
        seller: [
          { name: '입점사 목록', path: '/admin/seller/list', icon: 'bi bi-list-ul me-2' },
          { name: '입점신청 승인', path: '/admin/seller/signups', icon: 'bi bi-person-check me-2' },
          { name: '상품 승인', path: '/admin/seller/product-approval', icon: 'bi bi-patch-check me-2' },
          { name: '스토어 승인', path: '/admin/seller/store-approval', icon: 'bi bi-shop-window me-2' },
          { name: '광고 관리', path: '/admin/seller/ads', icon: 'bi bi-megaphone me-2' },
          { name: '입점사 문의', path: '/admin/seller/inquiries', icon: 'bi bi-chat-dots me-2' },
          { name: '입점사 공지', path: '/admin/seller/notices', icon: 'bi bi-megaphone me-2' },
          { name: '입점사 매출통계', path: '/admin/seller/sales-stats', icon: 'bi bi-graph-up-arrow me-2' },
          { name: '판매 정산', path: '/admin/seller/settlement', icon: 'bi bi-cash-stack me-2' }
        ],
        product: [
          { name: '제품 목록', path: '/admin/product/list', icon: 'bi bi-grid me-2' },
          { name: '제품 순서', path: '/admin/product/sort-order', icon: 'bi bi-sort-numeric-down me-2' },
          { name: '카테고리 관리', path: '/admin/product/categories', icon: 'bi bi-diagram-3 me-2' },
          // 🔥 2026-10-19 검색창 드롭다운(인기 검색어·추천 카테고리) 관리 + 검색 통계
          { name: '검색어 관리', path: '/admin/product/search-keywords', icon: 'bi bi-search me-2' }
        ],
        inventory: [
          { name: '재고 현황', path: '/admin/inventory/status', icon: 'bi bi-clipboard-data me-2' },
          { name: '가나전산 연동(조회)', path: '/admin/inventory/gana', icon: 'bi bi-hdd-network me-2' },
          { name: '용마로지스 배송조회', path: '/admin/inventory/yongma', icon: 'bi bi-truck me-2' }
        ],
        shoporders: [
          { name: '주문 목록', path: '/admin/shoporders', icon: 'bi bi-list-ul me-2' },
          { name: '신규 주문', path: '/admin/shoporders/new', icon: 'bi bi-plus-circle me-2' },
          { name: '배송 준비중', path: '/admin/shoporders/preparing', icon: 'bi bi-box-seam me-2' },
          { name: '배송중', path: '/admin/shoporders/shipping', icon: 'bi bi-truck me-2' },
          { name: '배송완료', path: '/admin/shoporders/delivered', icon: 'bi bi-check-circle me-2' },
          { name: '취소/반품/교환', path: '/admin/shoporders/cancelled', icon: 'bi bi-x-circle me-2' }
        ],
        order: [
          { name: '수주 관리', path: '/admin/business/list', icon: 'bi bi-journal-plus me-2' },
          { name: '배송 현황', path: '/admin/business/shipping', icon: 'bi bi-truck me-2' },
          { name: '견적 목록', path: '/admin/business/estimates', icon: 'bi bi-file-earmark-text me-2' },
          { name: '견적 등록', path: '/admin/business/create', icon: 'bi bi-plus-circle me-2' }
        ],
        shipping: [
          { name: '배송 목록', path: '/admin/shipping/list', icon: 'bi bi-truck me-2' },
          { name: '출고 목록', path: '/admin/shipping/dispatch', icon: 'bi bi-box-arrow-up-right me-2' },
          { name: '송장 등록', path: '/admin/shipping/invoice', icon: 'bi bi-receipt-cutoff me-2' },
          { name: '반품 관리', path: '/admin/shipping/returns', icon: 'bi bi-arrow-return-left me-2' },
          { name: '교환 관리', path: '/admin/shipping/exchanges', icon: 'bi bi-arrow-repeat me-2' }
        ],
        promotion: [
          { name: '쿠폰 관리', path: '/admin/promotion/coupons', icon: 'bi bi-ticket-perforated me-2' },
          { name: '프로모션 관리', path: '/admin/promotion/list', icon: 'bi bi-megaphone me-2' }
        ],
        design: [
          { name: '메인디자인 관리', path: '/admin/design/main', icon: 'bi bi-house me-2' },
          { name: '배너 관리', path: '/admin/design/banner', icon: 'bi bi-image me-2' },
          { name: '팝업 관리', path: '/admin/design/popup', icon: 'bi bi-window me-2' }
        ],
        payment: [
          { name: '정산 목록', path: '/admin/payment/settlements', icon: 'bi bi-cash-coin me-2' },
          { name: '외상 결제 현황', path: '/admin/payment/deferred', icon: 'bi bi-calendar-check me-2' },
          { name: '원장 미수금 결제', path: '/admin/payment/ledger-payments', icon: 'bi bi-credit-card-2-front me-2' },
          { name: '포인트 관리', path: '/admin/payment/point-management', icon: 'bi bi-star me-2' },
          { name: '포인트 내역', path: '/admin/payment/point-history', icon: 'bi bi-clock-history me-2' },
          { name: '포인트 설정', path: '/admin/payment/point-settings', icon: 'bi bi-gear me-2' }
        ],
        stats: [
          { name: '매출 통계', path: '/admin/stats/sales', icon: 'bi bi-bar-chart-line me-2' },
          { name: '주문 통계', path: '/admin/stats/statsorders', icon: 'bi bi-clipboard-data me-2' }
        ],
        support: [
          { name: '공지사항 관리', path: '/admin/support/notice', icon: 'bi bi-megaphone me-2' },
          { name: 'FAQ 관리', path: '/admin/support/faq', icon: 'bi bi-question-circle me-2' },
          { name: '1:1 문의 관리', path: '/admin/support/inquiry', icon: 'bi bi-chat-dots me-2' }
        ]
      }
    }
  },
  methods: {
    logout() {
      if (confirm('정말 로그아웃 하시겠습니까?')) {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        localStorage.removeItem('user_info');

        console.log('🔐 관리자 로그아웃 완료');
        alert('로그아웃되었습니다.');
        window.location.href = '/admin/login';
      }
    },
    setActiveMenu(menu) {
      this.activeMenu = menu.id;
      const subMenus = this.getSubMenus();
      if (subMenus.length > 0) {
        this.$router.push(subMenus[0].path);
      }
    },
    getSubMenus() {
      return this.subMenus[this.activeMenu] || [];
    },
    getActiveMenuName() {
      if (this.activeMenu === 'home') return '자주 가는 메뉴';
      const menu = this.mainMenus.find(m => m.id === this.activeMenu);
      return menu ? menu.name : '';
    },
    getActiveMenuIcon() {
      if (this.activeMenu === 'home') return 'bi bi-star';
      const menu = this.mainMenus.find(m => m.id === this.activeMenu);
      return menu ? menu.icon : 'bi bi-folder';
    },
    // 🔥 2026-10-21 주소 → 상단·왼쪽 메뉴 맞추기. 전에는 처음 열릴 때만 맞춰서,
    //    로고(홈)로 대시보드에 와도 이전 메뉴가 강조된 채 남았다.
    // 🔥 2026-10-21 바로가기는 운영 도메인으로 연다 (관리자는 IP 로 접속하므로 상대주소면 IP 로 열림). 개발 PC 는 그대로
    siteUrl(path) {
      const h = window.location.hostname;
      if (h === 'localhost' || h === '127.0.0.1') return path;
      return 'https://doctormarket.co.kr' + path;
    },
    syncMenuFromRoute(path) {
      let p = String(path || '');
      while (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);   // 끝의 '/' 제거
      if (p === '/admin') {
        this.activeMenu = 'home';
        return;
      }
      for (const menuId in this.subMenus) {
        if (menuId === 'home') continue;   // 바로가기 목록은 다른 메뉴 주소를 담고 있어 판정에서 뺀다
        if (this.subMenus[menuId].some(subMenu => p.startsWith(subMenu.path))) {
          this.activeMenu = menuId;
          return;
        }
      }
    },
    // 🔥 2026-10-21 상단 '처리할 일' 배지 — 조회 전용 API. 실패하면 배지만 숨기고 아무 영향 없음
    async loadHeaderCounts() {
      try {
        const res = await this.$axios.get('/api/admin/dashboard/header-counts');
        this.headerCounts = res && res.data ? res.data : null;
      } catch (e) {
        this.headerCounts = null;
        const st = e && e.response ? e.response.status : 0;
        if ((st === 401 || st === 403) && this.headerTimer) {   // 권한 없음 → 더 부르지 않음
          clearInterval(this.headerTimer);
          this.headerTimer = null;
        }
      }
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      try { localStorage.setItem('admin.sidebar.collapsed', this.sidebarCollapsed ? '1' : '0'); } catch (e) { /* noop */ }
      // 표·차트가 넓어진 폭에 맞게 다시 그리도록
      setTimeout(() => window.dispatchEvent(new Event('resize')), 220);
    },
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;
    },
    updateTime() {
      const now = new Date();
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };
      this.currentTime = now.toLocaleString('ko-KR', options);
    }
  },
  computed: {
    headerBadges() {
      const c = this.headerCounts || {};
      const list = [
        { key: 'today', label: '오늘 주문', count: c.todayOrders || 0, to: '/admin/shoporders', title: '오늘 들어온 주문 (결제완료·여신 기준)' },
        { key: 'deposit', label: '입금대기', count: c.depositWaiting || 0, to: { path: '/admin/shoporders', query: { status: 'PAYMENT_WAITING' } }, hot: true, title: '무통장·가상계좌 입금 확인 대기' },
        { key: 'ship', label: '배송준비', count: c.toShip || 0, to: '/admin/shoporders/preparing', hot: true, title: '지금 출고할 주문 (결제완료 + 여신 주문) — 배송준비중 화면과 같은 건수' },
        { key: 'inq', label: '문의', count: c.inquiries || 0, to: '/admin/support/inquiry', hot: true, title: '1:1 문의 답변 대기' },
        { key: 'approval', label: '상품승인', count: c.productApprovalPending || 0, to: '/admin/seller/product-approval', hot: true, title: '입점사 상품 승인 대기' },
        { key: 'apply', label: '입점신청', count: c.sellerApplications || 0, to: '/admin/seller/signups', hot: true, title: '입점 신청 승인 대기' }
      ];
      if (c.ganaSendFailed) {
        list.push({ key: 'gana', label: '가나 전송실패', count: c.ganaSendFailed, to: { path: '/admin/shoporders', query: { gana: 'FAILED' } }, hot: true, title: '가나전산 주문 전송 실패' });
      }
      return list;
    }
  },
  watch: {
    '$route.path'(path) {
      this.syncMenuFromRoute(path);
    }
  },
  created() {
    this.syncMenuFromRoute(this.$route.path);
    this.updateTime();
    this.timeInterval = setInterval(this.updateTime, 1000);
    this.loadHeaderCounts();
    this.headerTimer = setInterval(() => { if (!document.hidden) this.loadHeaderCounts(); }, 60000);
  },
  beforeUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
    if (this.headerTimer) {
      clearInterval(this.headerTimer);
    }
  }
}
</script>

<style scoped>
/* Oracle ERP 스타일 관리자 레이아웃 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.oracle-admin-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  font-family: 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ==================== 헤더 스타일 ==================== */
.oracle-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* 유틸리티 바 */
.header-utility-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 24px;
  background: linear-gradient(135deg, #1a1f36 0%, #0f1225 100%);
  color: #a0aec0;
  font-size: 12px;
}

.utility-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.system-status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  font-size: 8px;
  color: #48bb78;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.utility-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.current-time,
.admin-name {
  display: flex;
  align-items: center;
  gap: 6px;
}

.current-time i,
.admin-name i {
  color: #0066cc;
}

.utility-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #e2e8f0;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.utility-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
  color: #fca5a5;
}

/* 메인 헤더 */
.header-main {
  display: flex;
  align-items: center;
  padding: 0 24px;
  height: 60px;
  background: linear-gradient(135deg, #0066cc 0%, #004999 100%);
}

.header-brand {
  flex-shrink: 0;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: white;
}

.brand-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.brand-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.brand-subtitle {
  font-size: 10px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* 메인 네비게이션 */
.header-nav {
  flex: 1;
  margin-left: 40px;
  overflow-x: auto;
}

.nav-items {
  display: flex;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.nav-item.active {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  box-shadow: inset 0 -2px 0 #00d4ff;
}

.nav-item i {
  font-size: 14px;
}

/* 모바일 메뉴 토글 */
.mobile-menu-toggle {
  display: none;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: auto;
}

/* 모바일 네비게이션 */
.mobile-nav {
  display: none;
  flex-direction: column;
  background: #004999;
  padding: 10px;
  gap: 4px;
}

.mobile-nav.show {
  display: flex;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.2s;
}

.mobile-nav-item:hover,
.mobile-nav-item.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

/* ==================== 메인 컨테이너 ==================== */
.oracle-main-container {
  display: flex;
  flex: 1;
  min-height: calc(100vh - 200px);
}

/* ==================== 사이드바 ==================== */
.oracle-sidebar {
  width: 240px;
  background: #ffffff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.sidebar-header i {
  font-size: 18px;
  color: #0066cc;
}

.sidebar-nav {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  color: #475569;
  text-decoration: none;
  font-size: 13px;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s;
  position: relative;
}

.sidebar-link i:first-child {
  font-size: 16px;
  width: 20px;
  text-align: center;
  color: #64748b;
  transition: color 0.2s;
}

.sidebar-link span {
  flex: 1;
}

.sidebar-link .arrow-icon {
  font-size: 10px;
  opacity: 0;
  transition: all 0.2s;
  color: #94a3b8;
}

.sidebar-link:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.sidebar-link:hover i:first-child {
  color: #0066cc;
}

.sidebar-link:hover .arrow-icon {
  opacity: 1;
  transform: translateX(3px);
}

.sidebar-link.active {
  background: linear-gradient(135deg, #0066cc 0%, #004999 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 102, 204, 0.3);
}

.sidebar-link.active i:first-child {
  color: white;
}

.sidebar-link.active .arrow-icon {
  opacity: 1;
  color: rgba(255, 255, 255, 0.7);
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.quick-stats {
  display: flex;
  justify-content: center;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.stat-item i {
  color: #0066cc;
}

/* ==================== 메인 콘텐츠 ==================== */
.oracle-content {
  flex: 1;
  min-width: 0;
  background: #f0f2f5;
  overflow-x: auto;
}

.content-wrapper {
  padding: 24px;
  min-height: 100%;
}

/* ==================== 푸터 ==================== */
.oracle-footer {
  background: linear-gradient(135deg, #1a1f36 0%, #0f1225 100%);
  border-top: 3px solid #0066cc;
  padding: 20px 24px;
  margin-top: auto;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.footer-left {
  display: flex;
  align-items: center;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.footer-brand > i {
  font-size: 28px;
  color: #0066cc;
}

.footer-brand-text {
  display: flex;
  flex-direction: column;
}

.footer-title {
  font-size: 16px;
  font-weight: 600;
}

.footer-subtitle {
  font-size: 11px;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.footer-center {
  display: flex;
  align-items: center;
}

.footer-status {
  display: flex;
  gap: 12px;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: 12px;
  color: #a0aec0;
}

.status-badge.online {
  color: #48bb78;
}

.status-badge.online i {
  color: #48bb78;
}

.status-badge.version {
  background: linear-gradient(135deg, #0066cc 0%, #004999 100%);
  color: white;
  font-weight: 600;
}

.footer-right {
  text-align: right;
}

.footer-links {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
}

.footer-links a {
  color: #a0aec0;
  text-decoration: none;
  font-size: 12px;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: #0066cc;
}

.copyright {
  font-size: 11px;
  color: #64748b;
  margin: 0;
}

/* ==================== 반응형 디자인 ==================== */
@media (max-width: 1200px) {
  .nav-item {
    padding: 10px 12px;
    font-size: 12px;
  }

  .nav-item span {
    display: none;
  }

  .nav-item i {
    font-size: 16px;
  }
}

@media (max-width: 992px) {
  .header-nav {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .oracle-sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1001;
    transform: translateX(-100%);
    transition: transform 0.3s;
  }

  .oracle-sidebar.show {
    transform: translateX(0);
  }

  .oracle-content {
    margin-left: 0;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
  }

  .footer-right {
    text-align: center;
  }

  .footer-links {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .header-utility-bar {
    padding: 6px 16px;
    font-size: 11px;
  }

  .utility-right {
    gap: 12px;
  }

  .current-time {
    display: none;
  }

  .header-main {
    padding: 0 16px;
    height: 56px;
  }

  .brand-text {
    display: none;
  }

  .content-wrapper {
    padding: 16px;
  }

  .oracle-footer {
    padding: 16px;
  }

  .footer-status {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .utility-left {
    display: none;
  }

  .admin-name span {
    display: none;
  }

  .footer-brand-text {
    display: none;
  }
}

/* 스크롤바 스타일 */
.sidebar-nav::-webkit-scrollbar {
  width: 4px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 네비게이션 스크롤바 숨기기 */
.header-nav::-webkit-scrollbar {
  display: none;
}

.header-nav {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* 🔥 2026-10-21 대시보드 전용 바로가기 배너 (사이드바) */
.dm-side-banner { margin: 12px 12px 0; padding: 12px; border-radius: 8px; background: #1a1f36; color: #e2e8f0; }
.dm-side-banner-title { font-size: 11.5px; font-weight: 700; color: #a0aec0; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.dm-side-banner-link { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 6px; font-size: 12.5px; color: #e2e8f0; text-decoration: none; }
.dm-side-banner-link:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }

/* 🔥 2026-10-21 왼쪽 메뉴 접기·펼치기 (PC) */
.oracle-sidebar { transition: width 0.2s ease; }
.oracle-sidebar.is-collapsed { width: 0; border-right: 0; overflow: hidden; }
.sidebar-collapse-btn { margin-left: auto; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #64748b; cursor: pointer; }
.sidebar-collapse-btn i { font-size: 13px !important; color: #64748b !important; }
.sidebar-collapse-btn:hover { border-color: #cbd5e1; background: #f8fafc; }
.sidebar-open-tab { position: sticky; top: 12px; align-self: flex-start; z-index: 5; margin: 12px 0 0 0; display: flex; flex-direction: column; align-items: center; gap: 4px; width: 34px; padding: 10px 0; border: 1px solid #e2e8f0; border-left: 0; border-radius: 0 8px 8px 0; background: #fff; color: #0066cc; font-size: 11px; cursor: pointer; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06); }
.sidebar-open-tab span { writing-mode: vertical-rl; letter-spacing: 2px; color: #475569; }
.sidebar-open-tab:hover { background: #f8fafc; }
@media (max-width: 992px) {
  .sidebar-collapse-btn, .sidebar-open-tab { display: none; }
  .oracle-sidebar.is-collapsed { width: 240px; }
}

/* 🔥 2026-10-21 상단 '처리할 일' 배지 */
.todo-badges { display: flex; align-items: center; gap: 4px; margin-right: 6px; }
.todo-badge { display: inline-flex; align-items: center; gap: 5px; height: 22px; padding: 0 8px; border-radius: 11px; font-size: 11.5px; color: #cbd5e1; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.1); text-decoration: none; white-space: nowrap; }
.todo-badge b { font-weight: 700; color: #fff; }
.todo-badge:hover { background: rgba(255, 255, 255, 0.14); color: #fff; }
.todo-badge--hot b { color: #fbbf24; }
.todo-badge--zero { opacity: 0.55; }
@media (max-width: 1280px) { .todo-badges { display: none; } }

/* 🔥 2026-10-21 대시보드 자주 가는 메뉴 — 2열 타일 */
.quick-tiles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-bottom: 10px; }
.quick-tile { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; min-height: 66px; padding: 10px 6px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; color: #475569; text-decoration: none; text-align: center; transition: all 0.15s ease; }
.quick-tile i { font-size: 24px; color: #64748b; transition: color 0.2s; }
.quick-tile span { font-size: 11.5px; font-weight: 600; line-height: 1.25; word-break: keep-all; }
.quick-tile:hover { border-color: #cbd5e1; background: #f1f5f9; color: #1e293b; }
.quick-tile:hover i { color: #0066cc; }
.quick-tile--on { border-color: #cbd5e1; background: #f1f5f9; color: #1e293b; }
.quick-tile--on i { color: #334155; }
</style>
